import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ActionWidgetType, PermissionMap } from 'types/entity'
import { AppointmentForm } from './actionForms/AppointmentForm'
import { EMPTY_CONTACT, type EntityContact } from './actionForms/ContactCheckboxes'
import { DealForm } from './actionForms/DealForm'
import { DocumentForm } from './actionForms/DocumentForm'
import { EmailForm } from './actionForms/EmailForm'
import { FollowupForm } from './actionForms/FollowupForm'
import { NoteForm } from './actionForms/NoteForm'
import { SmsForm } from './actionForms/SmsForm'
import { TaskForm } from './actionForms/TaskForm'
import { TaxQuoteInvoiceForm } from './actionForms/TaxQuoteInvoiceForms'
import { VoiceForm } from './actionForms/VoiceForm'
import { WebformForm } from './actionForms/WebformForm'
import { WhatsappForm } from './actionForms/WhatsappForm'

export type { EntityContact } from './actionForms/ContactCheckboxes'

interface EntityActionWidgetProps {
  entityId: string
  entityType?: 'lead' | 'enquiry'
  contact?: EntityContact
  /** Role permissions — actions whose flag is false are hidden from the rail. */
  permissions?: PermissionMap
  onActionSuccess?: (msg: string) => void
}

/** Legacy rail rule: the enquiry edit page exposed only these actions. */
const ENQUIRY_ACTIONS: ActionWidgetType[] = ['sms', 'voice', 'email', 'whatsapp', 'notes']

/**
 * Permission key per action — the same mapping the legacy
 * `GetUserActionPermission` selector table and the Sales module's
 * MassOperation filter use. A missing map entry means "always shown".
 */
const ACTION_PERMISSION: Partial<Record<ActionWidgetType, keyof PermissionMap>> = {
  followup: 'AddFollowup',
  sms: 'SendSms',
  voice: 'SendVoice',
  email: 'SendMail',
  whatsapp: 'SendWhatsapp',
  notes: 'AddNote',
  document: 'UploadDocument',
  tasks: 'AddTask',
  appointment: 'AddAppointment',
  deal: 'AddDeal',
  leadtaxsetting: 'AddTax',
  QuotationSettings: 'AddQuotation',
  AddInvoice: 'AddInvoice',
  webform: 'AddWebform',
}

interface ActionItemDef {
  key: ActionWidgetType
  title: string
  tooltip: string
  iconClass: string
}

const ACTION_ITEMS: ActionItemDef[] = [
  { key: 'followup', title: 'Add Followup', tooltip: 'Add Followup', iconClass: 'fa fa-calendar-check-o' },
  { key: 'sms', title: 'Send SMS', tooltip: 'SMS', iconClass: 'fa fa-commenting-o' },
  { key: 'voice', title: 'Send Voice', tooltip: 'Send Voice', iconClass: 'fa fa-microphone' },
  { key: 'email', title: 'Send Mail', tooltip: 'Mail', iconClass: 'fa fa-envelope-o' },
  { key: 'whatsapp', title: 'WhatsApp', tooltip: 'WhatsApp', iconClass: 'fa fa-whatsapp' },
  { key: 'notes', title: 'Add Notes', tooltip: 'Add Notes', iconClass: 'fa fa-pencil-square-o' },
  { key: 'document', title: 'Upload Document', tooltip: 'Upload Document', iconClass: 'fa fa-upload' },
  { key: 'tasks', title: 'Add Task', tooltip: 'Add Task', iconClass: 'fa fa-tasks' },
  { key: 'appointment', title: 'Add Appointment', tooltip: 'Add Appointment', iconClass: 'fa fa-calendar' },
  { key: 'deal', title: 'Add Deal', tooltip: 'Add Deal', iconClass: 'fa fa-briefcase' },
  { key: 'leadtaxsetting', title: 'Tax Settings', tooltip: 'Add Tax Settings', iconClass: 'fa fa-money' },
  { key: 'QuotationSettings', title: 'Add Quotation', tooltip: 'Add Quotation', iconClass: 'fa fa-quote-right' },
  { key: 'AddInvoice', title: 'Add Invoice', tooltip: 'Add Invoice', iconClass: 'fa fa-file-text-o' },
  { key: 'webform', title: 'Webform', tooltip: 'Webform', iconClass: 'fa fa-list-alt' },
]

function ActionSvgIcon({ type }: { type: ActionWidgetType }) {
  const size = 18
  switch (type) {
    case 'followup':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <polyline points="9 16 11 18 15 14" />
        </svg>
      )
    case 'sms':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <line x1="8" y1="9" x2="16" y2="9" />
          <line x1="8" y1="13" x2="14" y2="13" />
        </svg>
      )
    case 'voice':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      )
    case 'email':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      )
    case 'whatsapp':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      )
    case 'notes':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      )
    case 'document':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      )
    case 'tasks':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="10" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="10" y1="18" x2="21" y2="18" />
          <polyline points="3 6 4 7 7 4" />
          <polyline points="3 12 4 13 7 10" />
          <polyline points="3 18 4 19 7 16" />
        </svg>
      )
    case 'appointment':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    case 'deal':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      )
    case 'leadtaxsetting':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      )
    case 'QuotationSettings':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
          <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
        </svg>
      )
    case 'AddInvoice':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      )
    case 'webform':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="7" y1="8" x2="17" y2="8" />
          <line x1="7" y1="12" x2="17" y2="12" />
          <line x1="7" y1="16" x2="13" y2="16" />
        </svg>
      )
    default:
      return null
  }
}

export function EntityActionWidget({
  entityId,
  entityType = 'lead',
  contact = EMPTY_CONTACT,
  permissions,
  onActionSuccess,
}: EntityActionWidgetProps) {
  // The legacy page auto-opened Add Followup beside the icon bar. Here the open action
  // is a drawer over the record (as in the Sales module), so it starts closed: a rail
  // row opens it.
  const [activeTab, setActiveTab] = useState<ActionWidgetType | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  // Sales-module semantics: `!== false` allowed — an absent flag never hides.
  const allowed = (key: ActionWidgetType) => {
    const permKey = ACTION_PERMISSION[key]
    if (!permKey || !permissions) return true
    return permissions[permKey] !== false
  }

  const visibleItems = (
    entityType === 'enquiry'
      ? ACTION_ITEMS.filter((item) => ENQUIRY_ACTIONS.includes(item.key))
      : ACTION_ITEMS
  ).filter((item) => allowed(item.key))

  const handleTabClick = (key: ActionWidgetType) => {
    if (activeTab === key && isOpen) {
      setIsOpen(false)
    } else {
      setActiveTab(key)
      setIsOpen(true)
    }
  }

  // Escape closes the drawer, like any dialog.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen])

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleSuccess = (msg: string) => {
    onActionSuccess?.(msg)
  }

  const activeItem = visibleItems.find((item) => item.key === activeTab && allowed(item.key))

  return (
    <div className="editEntityright-container">
      {/* Drawer Panel: portaled to <body> so it escapes the sticky sidebar's stacking
          context and sits over the page bar. A submit inside it (the action's own
          form) must not bubble into the edit page's <form>. */}
      {isOpen && activeTab && activeItem && createPortal(
        <div className="k-drawer-root" onSubmit={(e) => e.stopPropagation()}>
          <div className="k-drawer-scrim" onClick={handleClose} aria-hidden="true" />
          <div className="leadActionSection" role="dialog" aria-label={activeItem.title}>
          <div className="sectionHeader">
            <div className="sectionHeadTitle">{activeItem.title}</div>
            <button
              type="button"
              className="btnCloseLAS"
              onClick={handleClose}
              title="Close panel"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <div className="sectionBody">
            {activeTab === 'followup' && (
              <FollowupForm
                entityId={entityId}
                entityType={entityType}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'sms' && (
              <SmsForm
                entityId={entityId}
                entityType={entityType}
                contact={contact}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'voice' && (
              <VoiceForm
                entityId={entityId}
                entityType={entityType}
                contact={contact}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'email' && (
              <EmailForm
                entityId={entityId}
                entityType={entityType}
                contact={contact}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'whatsapp' && (
              <WhatsappForm
                entityId={entityId}
                entityType={entityType}
                contact={contact}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'notes' && (
              <NoteForm
                entityId={entityId}
                entityType={entityType}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'document' && (
              <DocumentForm
                entityId={entityId}
                entityType={entityType}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'tasks' && (
              <TaskForm entityId={entityId} onSuccess={handleSuccess} onCancel={handleClose} />
            )}

            {activeTab === 'appointment' && (
              <AppointmentForm
                entityId={entityId}
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'deal' && (
              <DealForm entityId={entityId} onSuccess={handleSuccess} onCancel={handleClose} />
            )}

            {activeTab === 'webform' && (
              <WebformForm entityId={entityId} onSuccess={handleSuccess} onCancel={handleClose} />
            )}

            {activeTab === 'leadtaxsetting' && (
              <TaxQuoteInvoiceForm
                entityId={entityId}
                type="tax"
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'QuotationSettings' && (
              <TaxQuoteInvoiceForm
                entityId={entityId}
                type="quote"
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}

            {activeTab === 'AddInvoice' && (
              <TaxQuoteInvoiceForm
                entityId={entityId}
                type="invoice"
                onSuccess={handleSuccess}
                onCancel={handleClose}
              />
            )}
          </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Right Vertical Action Icon Bar */}
      <div className="actionlist">
        <ul className="actionlistItem">
          {visibleItems.map((item) => {
            const isActive = isOpen && activeTab === item.key
            return (
              <li
                key={item.key}
                className={isActive ? 'btnactiveform' : ''}
                title={item.tooltip}
              >
                <button
                  type="button"
                  className="leadActionButton nav-link"
                  onClick={() => handleTabClick(item.key)}
                  aria-label={item.title}
                >
                  <ActionSvgIcon type={item.key} />
                  <span className="rail-label">{item.title}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
